/* eslint no-param-reassign: ["error", { "props": false }] */
import Vue from 'vue';
import Vuex from 'vuex';
import i18n from '@/locales';

Vue.use(Vuex);

const store = new Vuex.Store({
  // Initial state of application
  strict: true,
  state: {
    logoURL: null,
    hasBranding: false,
    config: {},
    clientInfo: {},
    envelope: {
      documents: [],
      title: null,
      id: null,
    },
    parties: [],
    colours: {},
    currentDocument: 0, // index
    currentPage: 0, // index
    currentField: 0, // index
    activeTabId: 0, // index
    isPopupActive: false,
    activePopupId: 0, // id
    sequenceGuideNumber: 1, // number
    signatureToUse: null,
    signatures: {
      drawn: null,
      typed: null,
      uploaded: null,
      signatureData: null,
      encodedJPG: null,
    },
    doneFields: {},
    isMobile: false,
    requestedEnvelope: {
      envelope_fingerprint: null,
      signature_fingerprint: null,
      preview_encrypt: null,
      isPreview: null,
      is_new_encrypt: null,
    },
  },
  mutations: {
    setCompanyConfig: (state, { config }) => {
      state.config = config;
      if (config.company_signature_default === 'upload') {
        state.signatureToUse = 'uploaded';
      } else {
        state.signatureToUse = config.company_signature_default;
      }
    },
    setClientInfo: (state, { info }) => {
      state.clientInfo = info;
    },
    setEnvelopeDetails: (state, { title, id }) => {
      state.envelope.title = title;
      state.envelope.id = id;
    },
    setDocuments: (state, { documents }) => {
      documents.forEach((doc) => {
        state.envelope.documents.push(doc);
      });
    },
    setParties: (state, { parties }) => {
      parties.forEach((party) => {
        state.parties.push(party);
      });
    },
    setColours: (state, { colours }) => {
      state.colours = colours;
    },
    setLogoURL: (state, { logo, hasBranding }) => {
      state.logoURL = logo;
      state.hasBranding = hasBranding;
    },
    setActiveStates: (state, { currentPage, currentField, activePopupId }) => {
      state.currentPage = currentPage;
      state.currentField = currentField;
      state.activePopupId = activePopupId;
    },
    setCurrentFieldVal: (state, { field, val, property }) => {
      field[property] = val;
    },
    removePrevFieldSignatures: (state, { field, property }) => {
      switch (property) {
        case 'drawn_json':
          field.content = null;
          field.upload = null;
          break;
        case 'content':
          field.drawn_json = null;
          field.upload = null;
          break;
        default:
          field.drawn_json = null;
          field.content = null;
      }
    },
    setActiveTab: (state, { activeTabId, tabProperty }) => {
      state.activeTabId = activeTabId;
      state.signatureToUse = tabProperty;
    },
    setFieldPopupState: (state, { isActive }) => {
      state.isPopupActive = isActive;
    },
    setSignature: (state, { signature, type, signatureData, encodedJPG }) => {
      state.signatures[type] = signature;
      if (signatureData) {
        state.signatures.signatureData = signatureData;
        state.signatures.encodedJPG = encodedJPG;
      }
    },
    setMobile: (state, { isMobile }) => {
      state.isMobile = isMobile;
    },
    addDoneField: (state, { field, fingerprint }) => {
      const hasFingerprint = Object.prototype.hasOwnProperty.call(state.doneFields, fingerprint);
      if (!hasFingerprint) {
        state.doneFields[fingerprint] = [];
      }
      const isFieldDone = state.doneFields[fingerprint].filter(
        doneField => doneField.id === field.id).length > 0;
      if (!isFieldDone) {
        state.doneFields[fingerprint].push(field);
      }
    },
    removeDoneField: (state, { field, fingerprint }) => {
      let index = 0;
      let documentFingerprint = fingerprint;
      if (!documentFingerprint) {
        const current = state.currentDocument;
        documentFingerprint = state.envelope.documents[current].fingerprint;
      }
      state.doneFields[documentFingerprint].filter((doneField, fieldIndex) => {
        if (doneField.id === field.id) {
          index = fieldIndex;
        }
        return false;
      });
      state.doneFields[fingerprint].splice(index, 1);
    },
    incrementSequence: (state, { newSequenceNumber }) => {
      state.sequenceGuideNumber = newSequenceNumber;
    },
    nextDocument: (state, { index }) => {
      state.currentDocument = index;
    },
    setRequestedEnvelope: (state, requestedEnvelope) => {
      state.requestedEnvelope = requestedEnvelope;
    },
  },
  actions: {
    setCurrentFieldVal: ({ commit, getters }, { val, id, property }) => {
      const field = getters.getField(id, false);
      commit('setCurrentFieldVal', { field, val, property });
    },
    removePrevFieldSignatures: ({ commit, getters }, { id, property }) => {
      const field = getters.getField(id, false);
      commit('removePrevFieldSignatures', { field, property });
    },
    addDoneField: ({ commit, getters }, { field }) => {
      const fingerprint = getters.currentDocumentFingerPrint;
      const isFieldGroup = field.group !== '';
      if (field.required === 'true') {
        if (isFieldGroup) {
          const allFields = getters.requiredFieldsForParty;
          const fieldsInGroup = allFields.filter(groupField => groupField.group === field.group);
          fieldsInGroup.forEach((item) => {
            commit('addDoneField', { field: item, fingerprint });
          });
        } else if (field.type === 'checkbox' || field.type === 'dropdown') {
          if (field.content === '0') {
            commit('removeDoneField', { field, fingerprint });
          } else {
            commit('addDoneField', { field, fingerprint });
          }
        } else {
          commit('addDoneField', { field, fingerprint });
        }
      }
    },
    nextDocument: ({ commit, getters, state }) => {
      let incompleteDoc = null;
      state.envelope.documents.forEach((doc, docIndex) => {
        const fingerprint = doc.fingerprint;
        const documentFields = getters.fieldsForDocument(docIndex);
        const doneFields = state.doneFields[fingerprint];
        documentFields.forEach((field) => {
          if (doneFields) {
            const isFieldDone = doneFields.filter(
              doneField => field.id === doneField.id).length > 0;
            if (!isFieldDone && incompleteDoc === null) {
              incompleteDoc = docIndex;
            }
          } else if (incompleteDoc === null) {
            incompleteDoc = docIndex;
          }
        });
      });
      commit('nextDocument', { index: incompleteDoc });
    },
  },
  getters: {
    requestedEnvelope: state => state.requestedEnvelope,
    config: state => property => state.config[property],
    removeFieldGroups: () => (fields) => {
      const newFields = [];
      let groupName;
      const otherFields = fields.filter(field => field.group === '');
      const groups = fields.filter(field => field.group !== '')
        .sort((a, b) => {
          const groupA = a.group.toUpperCase();
          const groupB = b.group.toUpperCase();
          if (groupA < groupB) {
            return -1;
          }
          if (groupA > groupB) {
            return 1;
          }
          return 0;
        });
      groups.forEach((field) => {
        if (field.group !== groupName) {
          newFields.push(field);
        }
        groupName = field.group;
      });
      return [...otherFields, ...newFields];
    },
    envelopeDetails: () => {
      const searchQuery = window.location.search.split('&');
      if (searchQuery.length > 1) {
        const envelopeFingerprint = searchQuery[0].replace('?envelope_fingerprint=', '');
        const signatureFingerprint = searchQuery[1].replace('signature_fingerprint=', '');
        return {
          envelope_fingerprint: envelopeFingerprint,
          signature_fingerprint: signatureFingerprint,
          isPreview: false,
        };
      }
      return {
        isPreview: true,
      };
    },
    document: state => index => state.envelope.documents[index],
    currentDocumentFingerPrint: (state, getters) => {
      const doc = getters.document(state.currentDocument);
      if (doc) {
        return doc.fingerprint;
      }
      return false;
    },
    currentPartyId: (state) => {
      let currentPartyId;
      state.parties.forEach((party) => {
        if (party.isSigner) {
          currentPartyId = party.party_id;
        }
      });
      return currentPartyId;
    },
    currentParty: (state, getters) => {
      const currentPartyId = getters.currentPartyId;
      return state.parties.filter(party => currentPartyId === party.party_id)[0];
    },
    allFields: (state) => {
      let fields = [];
      state.envelope.documents.forEach((doc) => {
        const documentFields = Object.values(doc.fields);
        const pages = Object.keys(doc.fields);
        documentFields.forEach((pageFields, page) => {
          pageFields.forEach((field, index) => {
            field.page = pages[page];
            field.index = index;
            fields.push(field);
          });
        });
      });
      // when merge fields come back, 2 always comeback one with value,
      // other without. This function sanitizes this bug
      fields = Object.values(fields.reduce((acc, cur) =>
        Object.assign(acc, { [cur.id]: cur }), {},
      ));
      return fields;
    },
    getField: (state, getters) => (id, returnValue = true, property) => {
      const allFields = getters.allFields;
      const matchedField = allFields.filter(field => parseInt(field.id, 10) === parseInt(id, 10));
      if (matchedField.length > 0) {
        if (returnValue) {
          return matchedField[0][property];
        }
        return matchedField[0];
      }
      return '';
    },
    fieldsForParty: (state, getters) => {
      const allFields = getters.allFields;
      const currentPartyId = getters.currentPartyId;
      return allFields.filter(field => field.party_id === currentPartyId);
    },
    requiredFieldsForParty: (state, getters) => {
      const fieldsForParty = getters.fieldsForParty;
      return fieldsForParty.filter((field) => {
        const isMergeField = field.is_merge_field;
        const isRequired = field.required === 'true';
        return isRequired && !isMergeField;
      });
    },
    fieldsForDocument: (state, getters) => (documentIndex = null) => {
      const currentDoc = documentIndex === null ? state.currentDocument : documentIndex;
      const doc = state.envelope.documents[currentDoc];
      const fields = [];
      if (doc) {
        const documentFields = Object.values(doc.fields);
        const currentPartyId = getters.currentPartyId;
        documentFields.forEach((pageFields) => {
          pageFields.forEach((field) => {
            const isForCurrentParty = field.party_id === currentPartyId;
            const isRequired = field.required === 'true';
            const isMergeField = field.is_merge_field;
            if (isForCurrentParty && isRequired && !isMergeField) {
              fields.push(field);
            }
          });
        });
      }
      return fields;
    },
    nextField: (state, getters) => () => {
      const fields = getters.fieldsForDocument();
      const fingerprint = getters.currentDocumentFingerPrint;
      const nextField = [];
      if (fields.length > 0) {
        fields.forEach((field) => {
          let isFieldDone = [];
          if (state.doneFields[fingerprint]) {
            isFieldDone = state.doneFields[fingerprint].length > 0 ?
              state.doneFields[fingerprint].filter(doneField => field.id === doneField.id) : [];
          }
          if (isFieldDone.length < 1 && nextField.length === 0) {
            nextField.push(field);
          }
        });
        return nextField[0];
      }
      return nextField;
    },
    doneFields: state => (fingerprint) => {
      if (fingerprint) {
        return state.doneFields[fingerprint];
      }
      const doneFieldsKeys = Object.keys(state.doneFields);
      const doneFields = state.doneFields;
      const fields = [];
      doneFieldsKeys.forEach((fingerprintKey) => {
        fields.push(...doneFields[fingerprintKey]);
      });
      return fields;
    },
    isDocumentDone: (state, getters) => () => {
      const fingerprint = getters.currentDocumentFingerPrint;
      let fieldsForCurrentDoc = getters.fieldsForDocument();
      const hasFingerprint = Object.prototype.hasOwnProperty.call(state.doneFields, fingerprint);
      let doneFields;
      if (fieldsForCurrentDoc.length === 0) {
        return true;
      }
      if (hasFingerprint) {
        doneFields = getters.removeFieldGroups(getters.doneFields(fingerprint));
        fieldsForCurrentDoc = getters.removeFieldGroups(fieldsForCurrentDoc);
      } else {
        return false;
      }
      if (fieldsForCurrentDoc.length !== doneFields.length) {
        return false;
      }
      return true;
    },
    currentSignature: state => state.signatures[state.signatureToUse],
    isCurrentSignatureImage: (state, getters) => {
      if (!getters.currentSignature) {
        return false;
      }
      return getters.currentSignature.indexOf('data:image') !== -1;
    },
    envelopeProgress: (state, getters) => () => {
      const allRequiredFields = getters.removeFieldGroups(getters.requiredFieldsForParty).length;
      let doneRequiredFields = 0;
      const doneFields = getters.removeFieldGroups(getters.doneFields());
      if (doneFields.length > 0) {
        doneFields.forEach(() => {
          doneRequiredFields += 1;
        });
      }
      if (allRequiredFields === 0) {
        return 100;
      }
      if (doneRequiredFields === 0) {
        return 0;
      }
      return Math.round((doneRequiredFields / allRequiredFields) * 100);
    },
    isEnvelopeComplete: (state, getters) => () => getters.envelopeProgress() === 100,
    footerNextButtonText: (state, getters) => () => {
      const isEnvelopeComplete = getters.isEnvelopeComplete();
      const isDocumentDone = getters.isDocumentDone();
      const nextField = getters.nextField();
      if (isEnvelopeComplete) {
        return state.config.company_consumer_regulations === '1' ? i18n.t('footer.order') : i18n.t('footer.submit');
      }
      if (isDocumentDone) {
        return i18n.t('footer.nextDocument');
      }
      if (nextField) {
        const hasTitle = nextField.title !== 'Untitled';
        const buttonText = hasTitle ? nextField.title : i18n.t(`fieldPopup.type.${nextField.type}`);
        return i18n.t('footer.nextField', { field: buttonText });
      }
      return '';
    },
  },
});

export default store;



// WEBPACK FOOTER //
// ./src/store/index.js